import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import {
    getProducts,
    getProductById,
    getProductVariations,
    ProductsQueryParams,
    ProductsResponse,
    Product,
    ProductVariation,
} from '../lib/store/products';

export const productKeys = {
    all: ['products'] as const,
    lists: () => [...productKeys.all, 'list'] as const,
    list: (params: ProductsQueryParams) => [...productKeys.lists(), params] as const,
    details: () => [...productKeys.all, 'detail'] as const,
    detail: (id: number) => [...productKeys.details(), id] as const,
    variations: (productId: number) => [...productKeys.detail(productId), 'variations'] as const,
};

export const useProducts = (
    params?: ProductsQueryParams,
    options?: Omit<UseQueryOptions<ProductsResponse>, 'queryKey' | 'queryFn'>,
) => {
    return useQuery<ProductsResponse>({
        queryKey: productKeys.list(params || {}),
        queryFn: () => getProducts(params),
        staleTime: 1000 * 60 * 5,
        retry: 1,
        ...options,
    });
};

export const useProduct = (
    id: number,
    options?: Omit<UseQueryOptions<Product>, 'queryKey' | 'queryFn'>,
) => {
    return useQuery<Product>({
        queryKey: productKeys.detail(id),
        queryFn: () => getProductById(id),
        enabled: !!id,
        staleTime: 1000 * 60 * 5,
        ...options,
    });
};

export const useProductVariations = (
    productId: number,
    options?: Omit<UseQueryOptions<ProductVariation[]>, 'queryKey' | 'queryFn'>,
) => {
    return useQuery<ProductVariation[]>({
        queryKey: productKeys.variations(productId),
        queryFn: () => getProductVariations(productId),
        enabled: !!productId,
        staleTime: 1000 * 60 * 5,
        ...options,
    });
};
